import { Edit, ListButton, RefreshButton, useForm, useSelect } from "@refinedev/antd";
import { DatePicker, Form, InputNumber, Select, Space, Tag } from "antd";
import { IResourceComponentsProps } from "@refinedev/core";
import { AppstoreOutlined, SettingOutlined } from "@ant-design/icons";

import { ICurrency, IMetadataOther, ISubscription } from "interfaces";
import React, { useEffect } from "react";
import { FormSection, FormFieldGrid } from "components/form";
import { PricingFields } from "components/metadata/PricingFields";
import { enrollmentDateFieldProps } from "components/metadata/enrollmentDate";
import {
  OTHER_SERVICE_CATALOG,
  OTHER_SERVICES_BY_PRODUCT,
  OTHER_STATUS_OPTIONS,
  OTHER_SUBSCRIPTION_TYPES,
  WIFI_DONGLE_VISIBLE,
} from "components/metadata/otherServiceCatalog";

export const MetadataOtherEdit: React.FC<IResourceComponentsProps> = () => {
  const {
    formProps,
    saveButtonProps,
    query,
    formLoading: isLoading,
  } = useForm<IMetadataOther>({
    action: "edit",
  });

  useEffect(() => {
    const form = formProps.form;
    const record = query?.data?.data;
    if (!form || !record) return;

    const recordAny = record as unknown as Record<string, unknown>;
    const updates: Record<string, string> = {};

    const normalizeSubscriptionType = (v: unknown): string | undefined => {
      if (typeof v !== "string") return undefined;
      if (v === "Flat Fee" || v === "Pay Per Use") return undefined;
      if (v.startsWith("Flat Fee Service")) return "Flat Fee";
      if (v === "Pay Per Use - Quotation") return "Pay Per Use";
      return undefined;
    };

    OTHER_SERVICE_CATALOG.forEach((svc) => {
      if (recordAny[`${svc.prefix}Currency`] == null) {
        updates[`${svc.prefix}Currency`] = "USD";
      }
      if (
        svc.hasWifiDongle &&
        recordAny[`${svc.prefix}WifiDongleCurrency`] == null
      ) {
        updates[`${svc.prefix}WifiDongleCurrency`] = "USD";
      }
      const normalized = normalizeSubscriptionType(
        recordAny[`${svc.prefix}SubscriptionType`],
      );
      if (normalized) {
        updates[`${svc.prefix}SubscriptionType`] = normalized;
      }
      if (
        svc.usesPayPerUseAmount &&
        recordAny[`${svc.prefix}DiscountPercentage`] != null
      ) {
        form.setFieldValue(`${svc.prefix}DiscountPercentage`, undefined);
      }
    });

    if (Object.keys(updates).length > 0) {
      form.setFieldsValue(updates);
    }
  }, [formProps.form, query?.data?.data]);

  const { selectProps: currencySelectProps } = useSelect<ICurrency>({
    resource: "currency",
    optionLabel: "code",
    optionValue: "code",
    pagination: { mode: "server" },
  });

  function SubscriptionSelectComponent() {
    const { selectProps } = useSelect<ISubscription>({
      resource: "subscription",
      optionLabel: "subscriptionName",
      optionValue: "id",
      filters: query?.data?.data?.subscription?.id
        ? [
            {
              field: "id",
              operator: "eq",
              value: query?.data?.data?.subscription?.id,
            },
          ]
        : [],
      pagination: { mode: "server" },
    });
    return (
      <Form.Item
        label="Subscription"
        name={["subscription", "id"]}
        rules={[{ required: true }]}
        initialValue={query?.data?.data?.subscription?.subscriptionName}
      >
        <Select {...selectProps} disabled />
      </Form.Item>
    );
  }

  return query?.isLoading ? (
    <React.Fragment>Loading...</React.Fragment>
  ) : (
    <Edit
      breadcrumb={false}
      saveButtonProps={saveButtonProps}
      headerProps={{
        onBack: () => window.history.back(),
        subTitle: "edit other service",
        tags: isLoading ? (
          <Tag color="orange">loading</Tag>
        ) : (
          <Tag color="green">live</Tag>
        ),
        ghost: true,
        extra: (
          <Space>
            <ListButton>Other Services</ListButton>
            <RefreshButton
              resource="metadata-other"
              recordItemId={query?.data?.data?.id}
            />
          </Space>
        ),
      }}
    >
      <Form {...formProps} layout="vertical">
        <Space direction="vertical" size={24} style={{ width: "100%" }}>
          <FormSection
            title="Subscription"
            subtitle="Link to active subscription"
            icon={<AppstoreOutlined />}
            variant="elevated"
          >
            {query?.isSuccess ? <SubscriptionSelectComponent /> : null}
          </FormSection>

          {OTHER_SERVICES_BY_PRODUCT.map((group) => (
            <FormSection
              key={group.product}
              title={group.product}
              subtitle={`${group.services.length} service${group.services.length > 1 ? "s" : ""}`}
              icon={group.icon}
              variant="elevated"
            >
              <Space
                direction="vertical"
                size={16}
                style={{ width: "100%" }}
              >
                {group.services.map((svc) => (
                  <FormSection
                    key={svc.prefix}
                    title={svc.service}
                    subtitle={svc.subtitle}
                    icon={<SettingOutlined />}
                    variant="subtle"
                  >
                    <FormFieldGrid columns={2}>
                      <Form.Item
                        label="Status"
                        name={`${svc.prefix}Status`}
                        rules={[{ required: false }]}
                      >
                        <Select options={OTHER_STATUS_OPTIONS} />
                      </Form.Item>
                      <Form.Item
                        label="Enrollment Date"
                        name={`${svc.prefix}EnrollmentDate`}
                        rules={[{ required: true }]}
                        {...enrollmentDateFieldProps}
                      >
                        <DatePicker
                          format="DD/MM/YYYY"
                          style={{ width: "100%" }}
                        />
                      </Form.Item>
                      <Form.Item
                        label="Subscription Type"
                        name={`${svc.prefix}SubscriptionType`}
                        rules={[{ required: false }]}
                      >
                        <Select options={OTHER_SUBSCRIPTION_TYPES} />
                      </Form.Item>
                      <Form.Item
                        label="Currency"
                        name={`${svc.prefix}Currency`}
                        rules={[{ required: false }]}
                      >
                        <Select {...currencySelectProps} allowClear />
                      </Form.Item>
                      <PricingFields
                        svc={svc}
                        form={formProps.form}
                        avcsFlatFeeAmount={query?.data?.data?.avcsFlatFeeAmount ?? null}
                        avcsFlatFeeCurrency={query?.data?.data?.avcsFlatFeeCurrency ?? null}
                      />
                    </FormFieldGrid>
                    {svc.hasWifiDongle && WIFI_DONGLE_VISIBLE && (
                      <FormFieldGrid columns={2}>
                        <Form.Item
                          label="Wi-Fi Dongle One-Time Cost"
                          name={`${svc.prefix}WifiDongleOneTimeCost`}
                          rules={[{ required: false }]}
                        >
                          <InputNumber
                            min={0}
                            precision={2}
                            style={{ width: "100%" }}
                          />
                        </Form.Item>
                        <Form.Item
                          label="Wi-Fi Dongle Currency"
                          name={`${svc.prefix}WifiDongleCurrency`}
                          rules={[{ required: false }]}
                        >
                          <Select {...currencySelectProps} allowClear />
                        </Form.Item>
                      </FormFieldGrid>
                    )}
                  </FormSection>
                ))}
              </Space>
            </FormSection>
          ))}
        </Space>
      </Form>
    </Edit>
  );
};
